'use client'

import _ from 'lodash'
import { deleteCookie } from 'cookies-next/client'
import { twMerge } from 'tailwind-merge'
import { useState, useEffect } from 'react'
import { Common, Vehicle, lookup } from '@cruz-com/lookup'
import { XMarkIcon } from '@heroicons/react/20/solid'
import { Button, Divider, Modal, Text } from '@/components'
import { useGlobalStore } from '@/store/provider'
import { myCruzApi } from '@/api'


type Props = {
  ignoredFilters?: string[]
  vCategory?: string
  query: Vehicle.Query
  onFilterChanged: (query: Vehicle.Query) => void
}

const ignoreCompareKeys = ['page_size', 'page_number', 'sort', 'sort_id', 'zip_code', 'radius']

// controlled from _searchVehicle
export function VehicleAppliedFilters({ ignoredFilters, vCategory, query, onFilterChanged }: Props) {
  const { user, setUser, modalSignIn, setModalSignIn, setToast } = useGlobalStore((state) => state)
  const [showSaveSearch, setShowSaveSearch] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [searchName, setSearchName] = useState('')
  const [errMsg, setErrMsg] = useState(null)
  const [isSearchSaved, setIsSearchSaved] = useState(checkIsSearchSaved())

  function checkIsSearchSaved() {
    if (_.isEmpty(user)) { return false }
    const d = user?.saved_vehicle_search_list
    if (_.isEmpty(d)) { return false }
    const a = _.omit(query, ignoreCompareKeys)
    const x = _.find(d, (o) => {
      const b = _.omit(o.query, ignoreCompareKeys)
      if (vCategory !== (o?.type || '')) { return null }
      if (_.isEqual(a, b)) { return o }
    })
    return !_.isEmpty(x)
  }

  useEffect(() => {
    setIsSearchSaved(checkIsSearchSaved())
  }, [query, user])

  // save search
  function handleSaveSearch() {
    if (_.isEmpty(user)) {
      return setModalSignIn({ show: true, saveSearch: true })
    }
    setShowSaveSearch(true)
  }

  useEffect(() => {
    if (modalSignIn?.saveSearch && !_.isEmpty(user)) {
      setShowSaveSearch(true)
      setModalSignIn({ show: false })
      setToast({ type: 'success', message: 'Successfully saved' })
    }
  }, [modalSignIn?.saveSearch, user])

  async function saveSearch() {
    // if (_.isEmpty(searchName)) { return }
    setIsSaving(true)
    setErrMsg(null)
    try {
      const { data } = await myCruzApi.saveSearch.save({
        name: searchName,
        type: vCategory,
        query
      })
      setShowSaveSearch(false)
      setUser(_.merge(user, data), null)
      setTimeout(() => {
        setSearchName('')
      }, 10)
    } catch (err: any) {
      setErrMsg(err?.message || 'Something went wrong. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  // lookup value
  const {
    condition, bodyType, driveType, fuelType,
    engineAspiration, transmission, colorGroup, country, saleStatus,
  } = lookup.vehicle

  // remove filter
  function removeFilter(name: string) {
    if (name === 'mmt1') {
      // delete cookie: "defaultVehicleQuery"
      deleteCookie('defaultVehicleQuery')
    }
    onFilterChanged(_.omit(query, name))
  }

  function Fc({ name, label, value, unit }: { name: string, label?: string, value: string | React.ReactNode, unit?: string }) {
    if (!value) { return null }
    return (
      <li className='flex items-center border rounded shadow-inner'>
        <div className='py-2 px-3'>
          <div className='text-xs font-light opacity-70 mb-0.5'>{label || 'Show'}</div>
          <div className='md:text-sm font-medium max-w-[200px] truncate'>
            {value}
            {unit && <span className='font-light text-xs ml-1'>{unit}</span>}
          </div>
        </div>
        <button
          className={
            twMerge('h-full border-l rounded-r p-1 hover:cursor-pointer transition duration-300',
              'hover:text-red-800 dark:hover:text-red-400',
              'bg-zinc-100 dark:bg-zinc-900 hover:bg-white dark:hover:bg-zinc-700')
          }
          onClick={() => removeFilter(name)}
        >
          <XMarkIcon className='size-4' />
        </button>
      </li >
    )
  }

  function getName(lkp: Common.Option[] | any[], val: any) {
    const x = _.split(val, ',')
    const y = _.pickBy(lkp, ({ id }) => _.includes(x, `${id}`))
    return _.map(y, 'name').join(', ')
  }

  function formatArray(val: string) {
    return val.split(',').join(', ')
  }

  const MMt = ({ name, mmt }: { name: string, mmt: string | null | undefined }) => {
    if (_.isEmpty(mmt)) { return null }
    const x = _.split(mmt, ',')
    const label = ['Make']
    const make = x[0]
    const model = _.compact(_.split(x[1], '|'))
    if (_.size(model) > 0) {
      label.push('Model')
    }
    const trim = _.compact(_.split(x[2], '|'))
    if (_.size(trim) > 0) {
      label.push('Trim')
    }
    return (
      <Fc name={name} label={label.join(', ')} value={
        <>
          {make}
          {model.length === 1 && <span className='pl-1'>{model[0]}</span>}
          {model.length > 1 && <span className='pl-1'>[{model.join(', ')}]</span>}
          {trim.length === 1 && <span className='pl-1'>{trim[0]}</span>}
          {trim.length > 1 && <span className='pl-1'>[{trim.join(', ')}]</span>}
        </>
      } />
    )
  }

  const MM = ({ name, mmt }: { name: string, mmt: string | null | undefined }) => {
    if (_.isEmpty(mmt)) { return null }
    const x = _.split(mmt, ',')
    const make = x[0]
    const model = _.split(x[1], '|')
    return (
      <Fc name={name} label='Make, Model' value={
        <>
          {make}
          {model.length === 1 && <span className='pl-1'>{model[0]}</span>}
          {model.length > 1 && <span className='pl-1'>[{model.join(', ')}]</span>}
        </>
      } />
    )
  }

  const Condition = () => {
    if (!query?.condition || ignoredFilters?.includes('condition')) { return null }
    return <Fc name='condition' label='Condition' value={getName(condition, query.condition)} />
  }

  const MinPrice = () => {
    if (!query?.price_min) { return null }
    return <Fc name='price_min' label='Minimum Price' value={`$${_.toNumber(query.price_min)?.toLocaleString()}`} />
  }

  const MaxPrice = () => {
    if (!query?.price_max) { return null }
    return <Fc name='price_max' label='Maximum Price' value={`$${_.toNumber(query.price_max)?.toLocaleString()}`} />
  }

  const Year = () => {
    if (!query?.year) { return null }
    return <Fc name='year' label='Year' value={query.year} />
  }

  const FromYear = () => {
    if (!query?.year_min) { return null }
    return <Fc name='year_min' label='From Year' value={query.year_min} />
  }

  const ToYear = () => {
    if (!query?.year_max) { return null }
    return <Fc name='year_max' label='To Year' value={query.year_max} />
  }

  const Mileage = () => {
    if (!query?.mileage) { return null }
    return <Fc name='mileage' label='Maximum Mileage' value={_.toNumber(query.mileage)?.toLocaleString()} unit='miles' />
  }

  const PriceDropped = () => {
    if (!query?.price_dropped) { return null }
    return <Fc name='price_dropped' value='Recently Price Dropped' />
  }

  const WithPhoto = () => {
    if (!query?.with_photo) { return null }
    return <Fc name='with_photo' value='With Photo(s)' />
  }

  const WithPrice = () => {
    if (!query?.with_price) { return null }
    return <Fc name='with_price' value='With Price' />
  }

  const Safest = () => {
    if (!query?.safest) { return null }
    return <Fc name='safest' value='5 Stars Safety Ratings' />
  }

  const BodyType = () => {
    if (!query?.body_type) { return null }
    return <Fc name='body_type' label='Type' value={getName(bodyType, query.body_type)} />
  }

  const SaleStatus = () => {
    if (!query?.sale_status) { return null }
    return <Fc name='sale_status' label='Sale Status' value={getName(saleStatus, query.sale_status)} />
  }

  const DriveType = () => {
    if (!query?.drive_type) { return null }
    return <Fc name='drive_type' label='Drive Type' value={getName(driveType, query.drive_type)} />
  }

  const FuelType = () => {
    if (!query?.fuel_type) { return null }
    return <Fc name='fuel_type' label='Fuel Type' value={getName(fuelType, query.fuel_type)} />
  }

  const FuelEconomy = () => {
    if (!query?.mpg) { return null }
    return <Fc name='mpg' label='Fuel Economy' value={`${query.mpg}+`} unit='mpg' />
  }

  const EngineAspiration = () => {
    if (!query?.engine_aspiration) { return null }
    return <Fc name='engine_aspiration' label='Engine Aspiration' value={getName(engineAspiration, query.engine_aspiration)} />
  }

  const EngineCylinder = () => {
    if (!query?.engine_cylinders) { return null }
    return <Fc name='engine_cylinders' label='Engine Cylinders' value={formatArray(query.engine_cylinders)} />
  }

  const EngineDisplacement = () => {
    if (!query?.engine_displacement) { return null }
    return <Fc name='engine_displacement' label='Engine Displacement' value={query.engine_displacement} />
  }

  const TransType = () => {
    if (!query?.trans_type) { return null }
    return <Fc name='trans_type' label='Transmission' value={getName(transmission, query.trans_type)} />
  }

  const TransGear = () => {
    if (!query?.trans_gears) { return null }
    return <Fc name='trans_gears' label='Gears' value={formatArray(query.trans_gears)} />
  }

  const Towing = () => {
    if (!query?.towing_capacity) { return null }
    return <Fc name='towing_capacity' label='Towing Capacity'
      value={`${_.toNumber(query.towing_capacity)?.toLocaleString()}+`}
      unit='lbs'
    />
  }

  const Acceleration = () => {
    if (!query?.acceleration) { return null }
    return <Fc name='acceleration' label='Acceleration' value={
      <div className='flex items-center gap-1'>
        <span className='font-light text-sm'>0-60 mpg in</span>
        <span className='font-medium'>{query.acceleration}</span>
        <span className='font-light text-sm'>sec.</span>
      </div>
    } />
  }

  const HorsePower = () => {
    if (!query?.horsepower) { return null }
    return <Fc name='horsepower' label='Horsepower' value={`${_.toNumber(query.horsepower)?.toLocaleString()}+`} unit='hp' />
  }

  const Torque = () => {
    if (!query?.torque) { return null }
    return <Fc name='torque' label='Torque' value={`${query.torque}+`} unit='ft.lbf' />
  }

  const ExtColor = () => {
    if (!query?.exterior_color) { return null }
    return <Fc name='exterior_color' label='Exterior Color' value={getName(colorGroup, query.exterior_color)} />
  }

  const IntColor = () => {
    if (!query?.interior_color) { return null }
    return <Fc name='interior_color' label='Interior Color' value={getName(colorGroup, query.interior_color)} />
  }

  const Upholstery = () => {
    if (!query?.upholstery) { return null }
    return <Fc name='upholstery' label='Upholstery' value={formatArray(query.upholstery)} />
  }

  const MaxSeating = () => {
    if (!query?.max_seating) { return null }
    return <Fc name='max_seating' label='Max Seating' value={formatArray(query.max_seating)} />
  }

  const Doors = () => {
    if (!query?.doors) { return null }
    return <Fc name='doors' label='Doors' value={formatArray(query.doors)} />
  }

  const Size = () => {
    if (!query?.size) { return null }
    return <Fc name='size' label='Size' value={formatArray(query.size)} />
  }

  const BedLength = () => {
    if (!query?.bed_length) { return null }
    return <Fc name='bed_length' label='Bed Length' value={`${query.bed_length}+`} unit='in.' />
  }

  const Feature = () => {
    if (!query?.feature) { return null }
    return <Fc name='feature' label='Feature' value={formatArray(query.feature)} />
  }

  const AssembledIn = () => {
    if (!query?.assembled_in) { return null }
    return <Fc name='assembled_in' label='Assembled In' value={getName(country, query.assembled_in)} />
  }

  const Seller = () => {
    if (!query?.seller) { return null }
    const x = _.toNumber(query?.seller)
    if (![1, 2].includes(x)) { return null }
    return <Fc name='seller' label='Seller' value={x === 1 ? 'Dealer' : 'Private'} />
  }

  const xFilters = _.chain(query)
    .omit(_.concat(ignoredFilters || [], ['zip_code', 'radius', 'location_id', 'seats_id', 'price_id', 'sort_value', 'sort_page_number']))
    .omitBy(_.isEmpty)
    .value()
  if (_.isEmpty(xFilters)) { return null }

  return (
    <>
      <Modal open={showSaveSearch} setOpen={setShowSaveSearch}>
        <div className='w-sm'>
          <Text label='Name this Search' compact={false} name='name' id='name' maxLength={150} autoFocus={true}
            value={searchName} onChange={(e) => setSearchName(e.target.value)}
          />
        </div>
        {
          errMsg &&
          <div className='form-error-message'>{errMsg}</div>
        }
        <div className='mt-6 flex justify-end gap-x-3'>
          <Button type='button' variant='outline' onClick={() => setShowSaveSearch(false)} className='w-28 h-8'>
            Cancel
          </Button>
          <Button type='button' className='w-28 h-8' working={isSaving} onClick={saveSearch}>
            Save
          </Button>
        </div>
      </Modal>
      <ul role='list' className='flex flex-wrap gap-4 pt-1 pb-2'>
        <Condition />
        <BodyType />
        <FromYear />
        <ToYear />
        <Year />
        <MMt name='mmt1' mmt={query?.mmt1} />
        <MMt name='mmt2' mmt={query?.mmt2} />
        <MMt name='mmt3' mmt={query?.mmt3} />
        <MM name='mm1' mmt={query?.mm1} />
        <MM name='mm2' mmt={query?.mm2} />
        <MM name='mm3' mmt={query?.mm3} />
        <Mileage />
        <MinPrice />
        <MaxPrice />
        <PriceDropped />
        <WithPhoto />
        <WithPrice />
        <Safest />
        <SaleStatus />
        <DriveType />
        <FuelType />
        <FuelEconomy />
        <EngineAspiration />
        <EngineCylinder />
        <EngineDisplacement />
        <TransType />
        <TransGear />
        <Towing />
        <Acceleration />
        <HorsePower />
        <Torque />
        <ExtColor />
        <IntColor />
        <Upholstery />
        <MaxSeating />
        <Doors />
        <BedLength />
        <Size />
        <Feature />
        <AssembledIn />
        <Seller />
        <Button type='button' className='h-8 place-self-end' disabled={isSearchSaved} onClick={handleSaveSearch}>
          {isSearchSaved ? 'Saved' : 'Save Search'}
        </Button>
      </ul>
      <Divider className='mt-2 mb-6' />
    </>
  )
}